import { CROP_PRESETS } from "@/utils/cropPresets";

export const SECTION_TYPES = {
  hero_carousel: {
    label: 'Hero Carousel',
    icon: 'bi-images',
    description: 'Large rotating banner slides at the top of the page, with optional side promo banners.',
    hasItems: true,
  },
  category_grid: {
    label: 'Category Grid',
    icon: 'bi-grid-3x3-gap',
    description: 'Category shortcuts shown as circles, tiles or cards.',
  },
  product_carousel: {
    label: 'Product Carousel',
    icon: 'bi-collection',
    description: 'A horizontally scrolling row of products (new arrivals, best sellers, a category, a brand, hand-picked...).',
  },
  product_grid: {
    label: 'Product Grid',
    icon: 'bi-grid',
    description: 'Products laid out in a grid.',
  },
  banner: {
    label: 'Promo Banner',
    icon: 'bi-card-image',
    description: 'A single wide promotional banner. Add several images to rotate them.',
    hasItems: true,
  },
  banner_grid: {
    label: 'Banner Grid',
    icon: 'bi-layout-three-columns',
    description: '2–4 promotional tiles side by side (e.g. deals, seasonal offers).',
    hasItems: true,
  },
  brand_strip: {
    label: 'Brand Strip',
    icon: 'bi-award',
    description: 'Brand logos customers can click to shop by brand.',
  },
  features: {
    label: 'Features / USP Bar',
    icon: 'bi-patch-check',
    description: 'Icons with short selling points like free delivery or secure payments.',
    hasItems: true,
  },
  text_block: {
    label: 'Text Block',
    icon: 'bi-text-paragraph',
    description: 'A heading with text, e.g. an announcement or welcome note.',
  },
  newsletter: {
    label: 'Newsletter / Call to Action',
    icon: 'bi-envelope-paper',
    description: 'A call to action strip that invites customers to create an account.',
  },
}

export const PRODUCT_SOURCES = {
  new_arrivals: 'New arrivals',
  best_sellers: 'Best sellers (all time)',
  trending: 'Trending (last 30 days)',
  high_stock: 'Great deals (high stock)',
  category: 'From a category',
  brand: 'From a brand',
  tag: 'Products with a tag',
  manual: 'Hand-picked products',
}

export const LINK_TYPES = {
  none: 'No link',
  category: 'Category',
  brand: 'Brand',
  product: 'Product',
  search: 'Search results',
  url: 'Custom URL',
}

export const ITEM_CROP = {
  hero_carousel: { main: CROP_PRESETS.heroSlide, side: CROP_PRESETS.sideBanner },
  banner: { main: CROP_PRESETS.banner },
  banner_grid: { main: CROP_PRESETS.bannerTile },
}

export const FEATURE_ICONS = [
  'truck', 'shield-check', 'patch-check', 'headset', 'credit-card', 'cash-coin', 'phone', 'gift', 'tag',
  'arrow-repeat', 'clock', 'geo-alt', 'star', 'heart', 'basket', 'bag-check', 'lightning-charge', 'award',
  'box-seam', 'percent', 'shop', 'people', 'emoji-smile', 'chat-dots',
]

export const COLOR_GROUPS = [
  {
    title: 'Brand',
    colors: {
      primary_color: 'Primary color',
      secondary_color: 'Accent color',
    },
  },
  {
    title: 'Header',
    colors: {
      topbar_bg: 'Announcement bar background',
      topbar_text: 'Announcement bar text',
      header_bg: 'Header background',
      header_text: 'Header text',
      nav_bg: 'Category menu background',
      nav_text: 'Category menu text',
    },
  },
  {
    title: 'Content',
    colors: {
      page_bg: 'Page background',
      card_bg: 'Card background',
      heading_color: 'Section headings',
      price_color: 'Prices',
      sale_badge_bg: 'Sale / stock badges',
      button_bg: 'Button background',
      button_text: 'Button text',
    },
  },
  {
    title: 'Footer',
    colors: {
      footer_bg: 'Footer background',
      footer_text: 'Footer text',
    },
  },
]

export const THEME_PRESETS = [
  {
    name: 'Royal Blue',
    theme: {
      primary_color: '#1e3a8a', secondary_color: '#f59e0b', topbar_bg: '#172554', topbar_text: '#ffffff',
      header_bg: '#1e3a8a', header_text: '#ffffff', nav_bg: '#ffffff', nav_text: '#1f2937', button_bg: '#1e3a8a',
      button_text: '#ffffff', price_color: '#111827', sale_badge_bg: '#dc2626', page_bg: '#f3f4f6', card_bg: '#ffffff',
      heading_color: '#111827', footer_bg: '#1e3a8a', footer_text: '#ffffff',
    },
  },
  {
    name: 'Fresh Green',
    theme: {
      primary_color: '#15803d', secondary_color: '#facc15', topbar_bg: '#14532d', topbar_text: '#ffffff',
      header_bg: '#ffffff', header_text: '#14532d', nav_bg: '#15803d', nav_text: '#ffffff', button_bg: '#15803d',
      button_text: '#ffffff', price_color: '#15803d', sale_badge_bg: '#dc2626', page_bg: '#f7faf7', card_bg: '#ffffff',
      heading_color: '#14532d', footer_bg: '#14532d', footer_text: '#ffffff',
    },
  },
  {
    name: 'Hypermarket Red',
    theme: {
      primary_color: '#c8102e', secondary_color: '#0e5aa7', topbar_bg: '#0e5aa7', topbar_text: '#ffffff',
      header_bg: '#ffffff', header_text: '#1f2937', nav_bg: '#c8102e', nav_text: '#ffffff', button_bg: '#c8102e',
      button_text: '#ffffff', price_color: '#c8102e', sale_badge_bg: '#0e5aa7', page_bg: '#f5f5f5', card_bg: '#ffffff',
      heading_color: '#111827', footer_bg: '#1f2937', footer_text: '#f3f4f6',
    },
  },
  {
    name: 'Sunset Orange',
    theme: {
      primary_color: '#ea580c', secondary_color: '#1e293b', topbar_bg: '#1e293b', topbar_text: '#ffffff',
      header_bg: '#ea580c', header_text: '#ffffff', nav_bg: '#fff7ed', nav_text: '#7c2d12', button_bg: '#ea580c',
      button_text: '#ffffff', price_color: '#c2410c', sale_badge_bg: '#1e293b', page_bg: '#fffaf5', card_bg: '#ffffff',
      heading_color: '#7c2d12', footer_bg: '#1e293b', footer_text: '#f8fafc',
    },
  },
  {
    name: 'Midnight',
    theme: {
      primary_color: '#111827', secondary_color: '#22d3ee', topbar_bg: '#000000', topbar_text: '#e5e7eb',
      header_bg: '#111827', header_text: '#f9fafb', nav_bg: '#1f2937', nav_text: '#f9fafb', button_bg: '#111827',
      button_text: '#ffffff', price_color: '#0e7490', sale_badge_bg: '#e11d48', page_bg: '#f3f4f6', card_bg: '#ffffff',
      heading_color: '#111827', footer_bg: '#030712', footer_text: '#d1d5db',
    },
  },
  {
    name: 'Berry Purple',
    theme: {
      primary_color: '#7e22ce', secondary_color: '#f472b6', topbar_bg: '#581c87', topbar_text: '#ffffff',
      header_bg: '#7e22ce', header_text: '#ffffff', nav_bg: '#faf5ff', nav_text: '#581c87', button_bg: '#7e22ce',
      button_text: '#ffffff', price_color: '#6b21a8', sale_badge_bg: '#db2777', page_bg: '#faf7fc', card_bg: '#ffffff',
      heading_color: '#3b0764', footer_bg: '#3b0764', footer_text: '#f3e8ff',
    },
  },
]

export const SOCIAL_NETWORKS = {
  facebook: { label: 'Facebook', icon: 'bi-facebook' },
  instagram: { label: 'Instagram', icon: 'bi-instagram' },
  twitter: { label: 'X / Twitter', icon: 'bi-twitter-x' },
  tiktok: { label: 'TikTok', icon: 'bi-tiktok' },
  youtube: { label: 'YouTube', icon: 'bi-youtube' },
  whatsapp: { label: 'WhatsApp', icon: 'bi-whatsapp' },
}
